import { useState, useEffect } from 'react';
import '../styles/Settings.css';
import RelativeSvgLoader from '../common/components/RelativeSvgLoader';
import BasicColors from './BasicColors';
import AdvancedColors from './AdvancedColors';
import Error from '../common/components/ErrorMessage';
import Success from '../common/components/SuccessMessage';
import { SETTINGS_API } from '../common/ResourcePaths';
import AuthData from '../common/AuthData';
import Post from '../services/postService';
import Fetch from '../services/fetchService';

function Colors() {
    const [loader, setLoader] = useState(false);
    const [activeView, setActiveView] = useState('basic'); // 'basic' or 'advanced'
    const [selectedPalette, setSelectedPalette] = useState(null);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [successTimer, setSuccessTimer] = useState(0);

    // Load the previously selected theme on component mount
    useEffect(() => {
        const loadSavedTheme = async () => {
            try {
                setLoader(true);
                const authData = AuthData.getAuthData();
                const url = `${authData.appFunctionUrl}${SETTINGS_API}/colors?code=${authData.azureFunctionKey}`;
                const colorsData = await Fetch(url, authData.clientId);
                
                if (colorsData && colorsData.selectedTheme) {
                    // Set the saved theme name for BasicColors to select
                    setSelectedPalette({ name: colorsData.selectedTheme });
                }
            } catch (err) {
                console.error('Failed to load saved theme:', err);
                // Don't show error to user, just log it
            } finally {
                setLoader(false);
            }
        };

        loadSavedTheme();
    }, []);

    const handlePaletteSelect = (palette) => {
        setSelectedPalette(palette);
        // For basic view, we just set the palette. User can save from there.
        // If you want to automatically switch to advanced view, uncomment the next line
        // setActiveView('advanced');
    };

    const handleSave = async (colorsToSave) => {
        setLoader(true);
        setError(null);
        setSuccess(null);

        try {
            const authData = AuthData.getAuthData();
            const url = `${authData.appFunctionUrl}${SETTINGS_API}/colors?code=${authData.azureFunctionKey}`;
            
            // Save colors and theme in single API call
            await Post(url, authData.clientId, colorsToSave);

            setSuccess(true);
            setSuccessTimer(10);
            const timerInterval = setInterval(() => {
                setSuccessTimer((prev) => {
                    if (prev <= 1) {
                        clearInterval(timerInterval);
                        setSuccess(false);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        } catch (err) {
            setError('Failed to save colors. Please try again.');
        } finally {
            setLoader(false);
        }
    };

    return (
        <div className="container" style={{ position: 'relative' }}>
            {loader && <RelativeSvgLoader />}
            {error && <Error message={error} />}
            {success && <Success message={`Colors saved successfully! (${successTimer}s)`} />}

            {/* Basic/Advanced Toggle Buttons */}
            <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                gap: '10px', 
                marginBottom: '20px', 
                paddingTop: '10px'
            }}>
                <button
                    type="button"
                    className={`settings-button ${activeView === 'basic' ? 'active' : ''}`}
                    onClick={() => setActiveView('basic')}
                    style={{ width: '120px' }}
                >
                    Basic
                </button>
                <button
                    type="button"
                    className={`settings-button ${activeView === 'advanced' ? 'active' : ''}`}
                    onClick={() => setActiveView('advanced')}
                    style={{ width: '120px' }}
                >
                    Advanced
                </button>
            </div>

            {/* Basic View */}
            {activeView === 'basic' && (
                <BasicColors 
                    loader={loader} 
                    onPaletteSelect={handlePaletteSelect}
                    onSave={handleSave}
                    selectedPalette={selectedPalette}
                />
            )}

            {/* Advanced View */}
            {activeView === 'advanced' && (
                <AdvancedColors 
                    loader={loader} 
                    setLoader={setLoader} 
                    palette={selectedPalette}
                    onSave={handleSave}
                    onColorChange={() => setSelectedPalette(null)}
                    setError={setError}
                    setSuccess={setSuccess}
                    setSuccessTimer={setSuccessTimer}
                />
            )}
        </div>
    );
}

export default Colors;
